import { Stack, Box, Button, Typography, useTheme, useMediaQuery } from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import PeopleOutlineIcon from '@mui/icons-material/PeopleOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PlansModal from './components/PlansModal/PlansModal';

import HomeHeader from './components/HomeHeader';
import HomeList from './components/HomeList';
import { useCallback, useEffect, useState, useRef } from 'react';
import { useSearchNotes } from '@/services/noteGoApi/noteGoApiComponents';
import type { NoteOut } from '@/services/noteGoApi/noteGoApiSchemas';
import { useAuthStore } from '@/stores/authStore';
import { useFirstNoteNudgeStore } from '@/stores/firstNoteNudgeStore';
import { AccountStatusTypes } from '@/helpers/accountStatus';
import { useNavigate, Navigate } from 'react-router';
import NoteCard from '@/components/NoteCard/NoteCard';
import { RouteMap } from '@/routes/Routes';
import { getTrialStatus } from '@/helpers/trialStatus';
import { logger } from '@/services/loggerService';
import { useSavedNotes } from '@/hooks/useSavedNotes';
import { errorReporter } from '@/services/errorReportingService';

const Home = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md')); // 900px and below
  const [userHaveNotes, setUserHaveNotes] = useState<boolean | null>(null);
  const { userToken, accountStatus, userLoggedIn, userData } = useAuthStore();
  const setFirstNoteNudge = useFirstNoteNudgeStore((state) => state.setActive);

  const {
    data: notesResponse,
    isSuccess,
    refetch: refetchNotes,
    isLoading: isNotesLoading,
  } = useSearchNotes(
    {
      headers: { authorization: `Bearer ${userToken}` },
      queryParams: {
        sort_by: 'created_at',
        sort_order: 'desc',
        limit: 5,
      },
    },
    {
      enabled: !!userToken && userLoggedIn,
    },
  );

  useEffect(() => {
    logger.debug(
      'notes',
      `Notes query: isSuccess=${isSuccess}, isLoading=${isNotesLoading}, notesLength=${notesResponse?.notes?.length ?? 'undefined'}`,
    );
    if (notesResponse && logger.isEnabled()) {
      logger.debug('notes', 'Full notes response', notesResponse);
    }
    if (isSuccess && notesResponse) {
      setUserHaveNotes(notesResponse.notes.length > 0);
    }
  }, [isSuccess, notesResponse, isNotesLoading]);

  // Nudge brand-new users toward the header "New Note" button while they have
  // no notes. Clear it when leaving Home so the nudge stays Home-scoped.
  useEffect(() => {
    setFirstNoteNudge(userHaveNotes === false);
    return () => setFirstNoteNudge(false);
  }, [userHaveNotes, setFirstNoteNudge]);

  // Fetch most recent starred notes
  const {
    data: starredResponse,
    isSuccess: isStarredSuccess,
    refetch: refetchStarred,
  } = useSearchNotes(
    {
      headers: { authorization: `Bearer ${userToken}` },
      queryParams: {
        sort_by: 'created_at',
        sort_order: 'desc',
        limit: 5,
        favorite: true,
      },
    },
    {
      enabled: !!userToken && userLoggedIn,
    },
  );

  // Fetch most recent shared-with-me notes
  const { data: sharedResponse, isSuccess: isSharedSuccess } = useSearchNotes(
    {
      headers: { authorization: `Bearer ${userToken}` },
      queryParams: {
        sort_by: 'created_at',
        sort_order: 'desc',
        limit: 5,
        shared: true,
      },
    },
    {
      enabled: !!userToken && userLoggedIn,
    },
  );

  // Fetch recently watched (saved) notes
  const { savedNotes, isLoading: isSavedNotesLoading } = useSavedNotes(userData?.id);
  // Keep the share token alongside each note — watched notes are viewer-access
  // via /shared/:token, not /note/:id (the viewer doesn't own them).
  const [watchedNotes, setWatchedNotes] = useState<Array<{ note: NoteOut; token: string }>>([]);

  useEffect(() => {
    if (isSavedNotesLoading || savedNotes.length === 0) {
      if (!isSavedNotesLoading) setWatchedNotes([]);
      return;
    }

    let cancelled = false;
    const recentTokens = [...savedNotes]
      .sort((a, b) => new Date(b.savedAt).getTime() - new Date(a.savedAt).getTime())
      .slice(0, 5);

    const fetchAll = async () => {
      const results = await Promise.all(
        recentTokens.map(async ({ token }): Promise<{ note: NoteOut; token: string } | null> => {
          try {
            const res = await fetch(`${import.meta.env.VITE_API_URL}/shared/${token}`);
            if (!res.ok) return null;
            const data = await res.json();
            const noteData: NoteOut = data.note || data;
            return noteData.id ? { note: noteData, token } : null;
          } catch (error) {
            errorReporter.expected('Failed to fetch watched note for home', error, { token });
            return null;
          }
        }),
      );
      if (!cancelled) setWatchedNotes(results.filter((n): n is { note: NoteOut; token: string } => n !== null));
    };

    fetchAll();
    return () => {
      cancelled = true;
    };
  }, [savedNotes, isSavedNotesLoading]);

  const navigate = useNavigate();
  const [userHaveStars, setUserHaveStars] = useState(false);
  useEffect(() => {
    logger.debug(
      'notes',
      `Starred query: isSuccess=${isStarredSuccess}, starredLength=${starredResponse?.notes?.length ?? 'undefined'}`,
    );
    if (isStarredSuccess && starredResponse?.notes.length) {
      setUserHaveStars(true);
    }
  }, [isStarredSuccess, starredResponse]);

  const hasSharedNotes = isSharedSuccess && (sharedResponse?.notes?.length ?? 0) > 0;
  const hasWatchedNotes = watchedNotes.length > 0;

  // Stable callback refs — inline arrows break React.memo on NoteCard.
  const handleStarredFavoriteToggle = useCallback(() => {
    refetchStarred();
  }, [refetchStarred]);

  // Check if user came from plugin redirect - use ref to check only once
  const fromPluginRedirectRef = useRef<boolean | null>(null);
  if (fromPluginRedirectRef.current === null) {
    const pluginRedirectDestination = sessionStorage.getItem('pluginRedirectDestination');
    if (pluginRedirectDestination) {
      sessionStorage.removeItem('pluginRedirectDestination');
      fromPluginRedirectRef.current = true;
    } else {
      fromPluginRedirectRef.current = false;
    }
  }

  // Check if user is on trial and has no videos
  const trialStatus = getTrialStatus(userData);
  // Show welcome only for active trials with no notes
  const isNewTrialUser = trialStatus.isOnTrial && userHaveNotes === false;

  // Redirect new trial users to the welcome page (unless they came from plugin redirect)
  if (isNewTrialUser && !fromPluginRedirectRef.current) {
    return <Navigate to={RouteMap.TRIAL_WELCOME} replace />;
  }

  logger.debug(
    'ui',
    `Home render: userHaveNotes=${userHaveNotes}, userHaveStars=${userHaveStars}, isLoading=${isNotesLoading}`,
  );

  return (
    <>
      <Stack alignItems="center" pt={4} pb={4} px={isMobile ? 2 : 4} minHeight={'100%'} width={'100%'} gap={2}>
        <HomeHeader userHaveNotes={userHaveNotes} />
        <HomeList
          userHaveNotes={userHaveNotes}
          notes={notesResponse?.notes}
          refetchNotes={refetchNotes}
          isNotesLoading={isNotesLoading}
        />

        {userHaveStars && (
          <Stack maxWidth={1620} width="100%" alignItems="flex-start" sx={{ mt: 4 }} spacing={2}>
            <Stack width="100%" height={64} alignItems="center" justifyContent="space-between" flexDirection="row">
              <Stack spacing={1} alignItems="flex-start">
                <Typography variant="h5" fontWeight={700}>
                  Recently Favorited <StarIcon sx={{ color: '#FFD700', ml: 1 }} />
                </Typography>
                <Typography variant="body1">View your most recently favorited items here.</Typography>
              </Stack>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: isMobile ? 2 : 3,
                width: '100%',
              }}
              pb={2}
            >
              {starredResponse?.notes.map((note: NoteOut) => (
                <NoteCard
                  key={note.id}
                  noteId={note.id}
                  title={note.title || 'Untitled'}
                  tags={note.tags}
                  transcript={note.transcript_url ?? ''}
                  hasVideo={!!note.video_url}
                  createdAt={note.created_at}
                  processingType={note.processing_type}
                  thumbnailUrl={note.thumbnail_card_url || note.thumbnail_url || ''}
                  creatorUserId={note.creator_user_id}
                  sharedBy={note.shared_by}
                  isFavorite={note.is_favorite}
                  onFavoriteToggle={handleStarredFavoriteToggle}
                />
              ))}
            </Box>
            <Button variant="text" color="secondary" onClick={() => navigate(RouteMap.FAVORITES)}>
              View full list
            </Button>
          </Stack>
        )}

        {hasSharedNotes && (
          <Stack maxWidth={1620} width="100%" alignItems="flex-start" sx={{ mt: 4 }} spacing={2}>
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="h5" fontWeight={700}>
                Recently Shared With Me <PeopleOutlineIcon sx={{ color: 'primary.main', ml: 1 }} />
              </Typography>
              <Typography variant="body1">Notes others have recently shared with you.</Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: isMobile ? 2 : 3,
                width: '100%',
              }}
              pb={2}
            >
              {sharedResponse?.notes.map((note: NoteOut) => (
                <NoteCard
                  key={note.id}
                  noteId={note.id}
                  title={note.title || 'Untitled'}
                  tags={note.tags}
                  transcript={note.transcript_url ?? ''}
                  hasVideo={!!note.video_url}
                  createdAt={note.created_at}
                  processingType={note.processing_type}
                  thumbnailUrl={note.thumbnail_card_url || note.thumbnail_url || ''}
                  creatorUserId={note.creator_user_id}
                  sharedBy={note.shared_by}
                />
              ))}
            </Box>
            <Button variant="text" color="secondary" onClick={() => navigate(RouteMap.SHARED_WITH_ME)}>
              View full list
            </Button>
          </Stack>
        )}

        {hasWatchedNotes && (
          <Stack maxWidth={1620} width="100%" alignItems="flex-start" sx={{ mt: 4 }} spacing={2}>
            <Stack spacing={1} alignItems="flex-start">
              <Typography variant="h5" fontWeight={700}>
                Recently Watched <VisibilityOutlinedIcon sx={{ color: 'primary.main', ml: 1 }} />
              </Typography>
              <Typography variant="body1">Notes you've recently saved to watch.</Typography>
            </Stack>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                gap: isMobile ? 2 : 3,
                width: '100%',
              }}
              pb={2}
            >
              {watchedNotes.map(({ note, token }) => (
                <NoteCard
                  key={note.id}
                  noteId={note.id}
                  title={note.title || 'Untitled'}
                  tags={note.tags}
                  transcript={note.transcript_url ?? ''}
                  hasVideo={!!note.video_url}
                  createdAt={note.created_at}
                  processingType={note.processing_type}
                  thumbnailUrl={note.thumbnail_card_url || note.thumbnail_url || ''}
                  creatorUserId={note.creator_user_id}
                  sharedBy={note.shared_by}
                  navigateUrl={`/shared/${token}`}
                />
              ))}
            </Box>
            <Button variant="text" color="secondary" onClick={() => navigate(RouteMap.SAVED_NOTES)}>
              View full list
            </Button>
          </Stack>
        )}
      </Stack>
      {accountStatus !== AccountStatusTypes.ACTIVE && <PlansModal />}
    </>
  );
};

export default Home;
